import { useState } from 'react';
import { motion } from 'framer-motion';
import { Image as ImageIcon, Music, Code2, Pin, PinOff, Trash2, Download, FileJson, MoreVertical } from 'lucide-react';
import { cn, timeAgo, downloadBlob } from '../../lib/utils';
import GlassCard from '../ui/GlassCard';
import CodeBlock from '../generation/CodeBlock';
import Tilt from '../fx/Tilt';

const MODALITY = {
  IMAGE: { icon: ImageIcon, label: 'Image', color: 'text-brand-300' },
  MUSIC: { icon: Music, label: 'Music', color: 'text-accent-cyan' },
  CODE: { icon: Code2, label: 'Code', color: 'text-accent-fuchsia' },
};

/**
 * Universal generation preview card. When pin/delete/export handlers are
 * passed it renders an actions menu (used on the History page).
 */
export default function GenerationCard({ generation: g, onPin, onDelete, onExport, index = 0 }) {
  const [menu, setMenu] = useState(false);
  const meta = MODALITY[g.modality] || MODALITY.IMAGE;
  const Icon = meta.icon;
  const hasActions = onPin || onDelete || onExport;

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(g, null, 2)], { type: 'application/json' });
    downloadBlob(blob, `aether-${g.id}.json`);
    onExport?.(g);
    setMenu(false);
  };

  const downloadAsset = async () => {
    if (!g.outputUrl) return;
    try {
      const res = await fetch(g.outputUrl);
      const ext = g.modality === 'IMAGE' ? 'png' : g.params?.contentType?.split('/')[1] || 'flac';
      downloadBlob(await res.blob(), `aether-${g.id}.${ext}`);
    } catch {
      /* ignore */
    }
    setMenu(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.04, 0.3), ease: [0.16, 1, 0.3, 1] }}
      layout
    >
      <Tilt max={6} scale={1.01} className="h-full">
        <GlassCard hoverable className="group flex h-full flex-col overflow-hidden">
        {/* Preview */}
        <div className="relative">
          {g.modality === 'IMAGE' && g.outputUrl && (
            <img
              src={g.outputUrl}
              alt={g.prompt}
              loading="lazy"
              className="aspect-video w-full object-cover"
            />
          )}
          {g.modality === 'MUSIC' && (
            <div className="flex aspect-video w-full flex-col items-center justify-center gap-3 bg-gradient-to-br from-accent-cyan/10 to-brand-500/10 p-4">
              <Music className="h-8 w-8 text-accent-cyan" />
              {g.outputUrl && (
                <audio controls src={g.outputUrl} className="w-full max-w-[260px]">
                  <track kind="captions" />
                </audio>
              )}
            </div>
          )}
          {g.modality === 'CODE' && (
            <div className="max-h-44 overflow-hidden">
              <CodeBlock
                code={(g.outputText || '').slice(0, 600)}
                language={g.params?.language || 'javascript'}
                maxHeight={176}
              />
            </div>
          )}

          {g.pinned && (
            <span className="absolute left-2 top-2 rounded-full bg-black/50 p-1.5 backdrop-blur">
              <Pin className="h-3.5 w-3.5 text-amber-300" />
            </span>
          )}

          {hasActions && (
            <div className="absolute right-2 top-2">
              <button
                onClick={() => setMenu((m) => !m)}
                className="rounded-lg bg-black/40 p-1.5 text-white backdrop-blur transition-colors hover:bg-black/60"
                aria-label="Actions"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
              {menu && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -4 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  className="glass-strong absolute right-0 z-20 mt-1 w-36 rounded-lg p-1 shadow-soft"
                >
                  {onPin && (
                    <button
                      onClick={() => {
                        onPin(g);
                        setMenu(false);
                      }}
                      className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-content-muted hover:bg-white/5 hover:text-content"
                    >
                      {g.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                      {g.pinned ? 'Unpin' : 'Pin'}
                    </button>
                  )}
                  {g.outputUrl && (
                    <button
                      onClick={downloadAsset}
                      className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-content-muted hover:bg-white/5 hover:text-content"
                    >
                      <Download className="h-4 w-4" /> Download {g.modality === 'IMAGE' ? 'PNG' : 'audio'}
                    </button>
                  )}
                  <button
                    onClick={exportJson}
                    className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-content-muted hover:bg-white/5 hover:text-content"
                  >
                    <FileJson className="h-4 w-4" /> Export JSON
                  </button>
                  {onDelete && (
                    <button
                      onClick={() => {
                        onDelete(g);
                        setMenu(false);
                      }}
                      className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-danger hover:bg-danger/10"
                    >
                      <Trash2 className="h-4 w-4" /> Delete
                    </button>
                  )}
                </motion.div>
              )}
            </div>
          )}
        </div>

        {/* Meta */}
        <div className="flex flex-1 flex-col p-4">
          <div className="mb-2 flex items-center gap-2 text-xs">
            <Icon className={cn('h-4 w-4', meta.color)} />
            <span className="font-medium text-content-muted">{meta.label}</span>
            <span className="text-content-subtle">· {timeAgo(g.createdAt)}</span>
          </div>
          <p className="line-clamp-2 text-sm text-content">{g.prompt}</p>
          <p className="mt-2 truncate font-mono text-[11px] text-content-subtle">{g.model}</p>
        </div>
        </GlassCard>
      </Tilt>
    </motion.div>
  );
}
